/**
 * Player - The player-controlled entity
 */

import { Entity } from './Entity';
import { Vector3 } from '../math/Vector3';
import { Vector2 } from '../math/Vector2';
import { Renderer } from '../core/Renderer';
import { HealthComponent } from './components/HealthComponent';
import { MovementComponent } from './components/MovementComponent';
import { StaminaComponent } from './components/StaminaComponent';
import { InventoryComponent } from '../items/InventoryComponent';
import { ItemStack, ToolItem, WeaponItem, WeaponType } from '../items/Item';
import { EventBus } from '../systems/EventBus';
import { renderPlayerSprite } from './PlayerSprite';

export class Player extends Entity {
  // Component references for quick access
  public health: HealthComponent;
  public movement: MovementComponent;
  public stamina: StaminaComponent;
  private inventoryRef: InventoryComponent | null = null;

  // Attack properties
  private attackCooldown: number = 0;
  private attackCooldownMax: number = 0.5;
  private attackRange: number = 1.5;
  private attackDamage: number = 10;
  private isAttacking: boolean = false;
  private attackTimer: number = 0;
  private attackDuration: number = 0.25;
  private attackProgress: number = 0; // 0-1 animation progress

  // Flying properties
  private canFly: boolean = false;
  private isFlying: boolean = false;
  private flyHeight: number = 0;
  private maxFlyHeight: number = 8; // tiles above ground (very high and obvious)
  private flySpeed: number = 5; // tiles per second to rise/fall
  private safeFlightHeight: number = 2; // height at which player is safe from enemies

  // Visual properties
  private size: number = 24;

  constructor(position: Vector3) {
    super(position);

    // Add components
    this.health = this.addComponent(new HealthComponent(100, 2, 5));
    this.movement = this.addComponent(new MovementComponent(6, 12));
    this.stamina = this.addComponent(new StaminaComponent(100, 25, 0.5));

    // Setup health events
    this.health.onDeath = () => this.onDeath();
    this.health.onDamage = (amount) => this.onDamage(amount);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);

    // Update attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= deltaTime;
    }

    // Update attack animation
    if (this.isAttacking) {
      this.attackTimer -= deltaTime;
      this.attackProgress = 1 - (this.attackTimer / this.attackDuration);
      if (this.attackTimer <= 0) {
        this.isAttacking = false;
        this.attackProgress = 0;
      }
    }

    // Update flying height
    if (this.canFly) {
      if (this.isFlying) {
        // Rise up
        this.flyHeight = Math.min(this.maxFlyHeight, this.flyHeight + this.flySpeed * deltaTime);
      } else {
        // Fall down
        this.flyHeight = Math.max(0, this.flyHeight - this.flySpeed * deltaTime);
      }
      // Update Z position
      this.position.z = this.flyHeight;
    }
  }

  /** Move the player */
  move(direction: Vector2): void {
    this.movement.move(direction);
  }

  /** Try to attack */
  attack(): boolean {
    if (this.attackCooldown > 0) return false;
    if (!this.stamina.use(10)) return false;

    this.attackCooldown = this.attackCooldownMax;
    this.isAttacking = true;
    this.attackDuration = this.getAttackDuration();
    this.attackTimer = this.attackDuration;
    this.attackProgress = 0;

    return true;
  }

  /** Get attack duration based on weapon type */
  private getAttackDuration(): number {
    const weaponType = this.getCurrentWeaponType();
    switch (weaponType) {
      case 'dagger': return 0.15;
      case 'spear': return 0.3;
      case 'hammer': return 0.4;
      case 'club': return 0.35;
      case 'scythe': return 0.35;
      default: return 0.25;
    }
  }

  /** Get current weapon type */
  getCurrentWeaponType(): WeaponType | null {
    if (this.inventoryRef) {
      const selectedItem = this.inventoryRef.getSelectedItem();
      if (selectedItem?.item instanceof WeaponItem) {
        return selectedItem.item.weaponType;
      }
    }
    return null;
  }

  /** Get attack position (in front of player) */
  getAttackPosition(): Vector3 {
    const dir = this.movement.getFacingDirection();
    return new Vector3(
      this.position.x + dir.x * this.attackRange,
      this.position.y + dir.y * this.attackRange,
      this.position.z
    );
  }

  getAttackDamage(): number {
    // Check if holding a weapon
    if (this.inventoryRef) {
      const selectedItem = this.inventoryRef.getSelectedItem();
      if (selectedItem?.item instanceof WeaponItem) {
        return this.attackDamage + selectedItem.item.damage;
      }
    }
    return this.attackDamage;
  }

  getAttackRange(): number {
    // Check if holding a weapon with custom range
    if (this.inventoryRef) {
      const selectedItem = this.inventoryRef.getSelectedItem();
      if (selectedItem?.item instanceof WeaponItem) {
        return selectedItem.item.range;
      }
    }
    return this.attackRange;
  }

  canAttack(): boolean {
    return this.attackCooldown <= 0 && this.stamina.hasStamina(10);
  }

  isCurrentlyAttacking(): boolean {
    return this.isAttacking;
  }

  setAttackDamage(damage: number): void {
    this.attackDamage = damage;
  }

  setInventoryRef(inventory: InventoryComponent): void {
    this.inventoryRef = inventory;
  }

  getHeldItem(): ItemStack | null {
    return this.inventoryRef?.getSelectedItem() ?? null;
  }

  render(renderer: Renderer): void {
    // Draw shadow (smaller when flying high)
    const shadowSize = this.flyHeight > 0
      ? Math.max(this.size / 6, this.size / 3 - this.flyHeight * 0.03)
      : this.size / 3;
    const shadowOpacity = this.flyHeight > 0
      ? Math.max(0.1, 0.3 - this.flyHeight * 0.02)
      : 0.3;
    renderer.drawCircle(
      new Vector3(this.position.x, this.position.y, 0),
      shadowSize,
      `rgba(0, 0, 0, ${shadowOpacity})`
    );

    // Draw pixel art player sprite
    const screenPos = renderer.worldToScreen(this.position);
    const spriteSize = this.size * 2.5 * renderer.getZoom();
    const flashDamage = this.health.isInvulnerable();
    const facing = this.movement.getFacingDirection();
    const ctx = renderer.getContext();

    // Draw flying glow effect when airborne
    if (this.flyHeight > 0.5) {
      const glowIntensity = Math.min(1, this.flyHeight / this.safeFlightHeight);
      const pulseTime = Date.now() / 500;
      const pulse = 0.7 + 0.3 * Math.sin(pulseTime);
      const glowSize = spriteSize * (1.2 + glowIntensity * 0.5) * pulse;

      // Outer glow (cyan/blue aura)
      const gradient = ctx.createRadialGradient(
        screenPos.x, screenPos.y, spriteSize * 0.3,
        screenPos.x, screenPos.y, glowSize
      );
      gradient.addColorStop(0, `rgba(100, 200, 255, ${0.4 * glowIntensity * pulse})`);
      gradient.addColorStop(0.5, `rgba(50, 150, 255, ${0.2 * glowIntensity * pulse})`);
      gradient.addColorStop(1, 'rgba(50, 100, 255, 0)');

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y, glowSize, 0, Math.PI * 2);
      ctx.fill();

      // Add sparkle particles around player when flying high
      if (this.isSafeFromEnemies()) {
        const sparkleCount = 6;
        for (let i = 0; i < sparkleCount; i++) {
          const angle = (pulseTime * 2 + i * Math.PI * 2 / sparkleCount) % (Math.PI * 2);
          const distance = spriteSize * (0.8 + 0.3 * Math.sin(pulseTime * 3 + i));
          const sparkleX = screenPos.x + Math.cos(angle) * distance;
          const sparkleY = screenPos.y + Math.sin(angle) * distance * 0.5; // Flattened for isometric
          const sparkleSize = 3 * renderer.getZoom() * (0.5 + 0.5 * Math.sin(pulseTime * 4 + i * 2));

          ctx.fillStyle = `rgba(200, 230, 255, ${0.8 * pulse})`;
          ctx.beginPath();
          ctx.arc(sparkleX, sparkleY, sparkleSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    renderPlayerSprite(
      ctx,
      screenPos.x,
      screenPos.y,
      spriteSize,
      facing.x,
      facing.y,
      this.isAttacking,
      flashDamage
    );

    // Draw held item
    const dir = this.movement.getFacingDirection();
    this.renderHeldItem(renderer, dir);

    // Draw attack effect
    if (this.isAttacking) {
      this.renderAttackEffect(renderer, dir);
    }

    // Draw health bar if damaged
    if (this.health.getHealthPercent() < 1) {
      renderer.drawHealthBar(
        new Vector3(this.position.x, this.position.y, this.position.z + this.size * 2),
        this.health.getCurrentHealth(),
        this.health.getMaxHealth()
      );
    }
  }

  private renderHeldItem(renderer: Renderer, facing: Vector2): void {
    const heldItem = this.getHeldItem();
    if (!heldItem) return;

    const item = heldItem.item;

    // Calculate attack animation offset
    let attackOffset = 0;
    let attackAngleOffset = 0;
    if (this.isAttacking && item instanceof WeaponItem) {
      const progress = this.attackProgress;
      const weaponType = item.weaponType;

      if (weaponType === 'spear') {
        // Thrust forward animation
        attackOffset = Math.sin(progress * Math.PI) * 0.8;
      } else if (weaponType === 'dagger') {
        // Quick stab animation
        attackOffset = Math.sin(progress * Math.PI) * 0.5;
      } else if (weaponType === 'hammer' || weaponType === 'club') {
        // Overhead swing
        attackAngleOffset = Math.sin(progress * Math.PI) * 0.6;
      } else if (weaponType === 'scythe') {
        // Wide sweep
        attackAngleOffset = Math.sin(progress * Math.PI) * 0.8;
      } else {
        // Sword slash
        attackAngleOffset = Math.sin(progress * Math.PI) * 0.5;
      }
    }

    // Position the item to the side of the player based on facing direction
    const itemOffsetX = facing.y * 0.4 + facing.x * attackOffset;
    const itemOffsetY = -facing.x * 0.4 + facing.y * attackOffset;
    const itemZ = this.size * 0.6;

    const itemPos = new Vector3(
      this.position.x + itemOffsetX + facing.x * 0.3,
      this.position.y + itemOffsetY + facing.y * 0.3,
      this.position.z + itemZ + attackAngleOffset * 20
    );

    // Determine item color and shape based on type
    let itemColor = '#888';
    let itemWidth = 4;
    let itemHeight = 16;

    if (item instanceof ToolItem) {
      switch (item.toolType) {
        case 'axe':
          itemColor = '#8B4513'; // Brown handle
          itemWidth = 5;
          itemHeight = 20;
          break;
        case 'pickaxe':
          itemColor = '#696969'; // Gray
          itemWidth = 5;
          itemHeight = 18;
          break;
        default:
          itemColor = '#A0522D';
      }
    } else if (item instanceof WeaponItem) {
      // Different appearance per weapon type
      switch (item.weaponType) {
        case 'spear':
          itemColor = '#8B4513'; // Brown shaft
          itemWidth = 3;
          itemHeight = 32; // Longer
          break;
        case 'dagger':
          itemColor = '#A9A9A9'; // Dark silver
          itemWidth = 3;
          itemHeight = 14; // Shorter
          break;
        case 'hammer':
          itemColor = '#696969'; // Gray
          itemWidth = 5;
          itemHeight = 24;
          break;
        case 'club':
          itemColor = '#D2B48C'; // Tan
          itemWidth = 6;
          itemHeight = 22;
          break;
        case 'scythe':
          itemColor = '#2F4F4F'; // Dark slate
          itemWidth = 3;
          itemHeight = 30;
          break;
        case 'axe':
          itemColor = '#8B4513'; // Brown
          itemWidth = 5;
          itemHeight = 22;
          break;
        default: // sword
          itemColor = '#C0C0C0'; // Silver
          itemWidth = 4;
          itemHeight = 24;
      }
    }

    // Draw the item
    renderer.drawRect(itemPos, itemWidth, itemHeight, itemColor);

    // Draw weapon heads/tips
    if (item instanceof WeaponItem) {
      const tipPos = new Vector3(
        itemPos.x + facing.x * 0.2,
        itemPos.y + facing.y * 0.2,
        itemPos.z + itemHeight * 0.5
      );

      switch (item.weaponType) {
        case 'spear':
          // Spear tip
          renderer.drawRect(tipPos, 4, 8, '#708090');
          break;
        case 'hammer':
          // Hammer head
          renderer.drawRect(tipPos, 12, 8, '#505050');
          break;
        case 'club':
          // Club head (rounded top represented as wider rect)
          renderer.drawRect(tipPos, 10, 10, '#A0522D');
          break;
        case 'scythe':
          // Scythe blade
          const bladePos = new Vector3(tipPos.x + facing.y * 0.3, tipPos.y - facing.x * 0.3, tipPos.z);
          renderer.drawRect(bladePos, 14, 4, '#708090');
          break;
        case 'axe':
          // Axe head
          renderer.drawRect(tipPos, 10, 6, '#505050');
          break;
      }
    } else if (item instanceof ToolItem) {
      const tipPos = new Vector3(
        itemPos.x + facing.x * 0.2,
        itemPos.y + facing.y * 0.2,
        itemPos.z + itemHeight * 0.6
      );
      const tipColor = item.toolType === 'axe' ? '#555' : '#888';
      renderer.drawRect(tipPos, 8, 6, tipColor);
    }
  }

  private renderAttackEffect(renderer: Renderer, facing: Vector2): void {
    const weaponType = this.getCurrentWeaponType();
    const range = this.getAttackRange();
    const progress = this.attackProgress;

    // Calculate base attack position
    const baseX = this.position.x + facing.x * range * 0.7;
    const baseY = this.position.y + facing.y * range * 0.7;

    // Different effects based on weapon type
    if (weaponType === 'spear') {
      // Thrust line effect
      const thrustProgress = Math.sin(progress * Math.PI);
      const startPos = new Vector3(this.position.x, this.position.y, this.position.z + this.size * 0.5);
      const endPos = new Vector3(
        this.position.x + facing.x * range * thrustProgress,
        this.position.y + facing.y * range * thrustProgress,
        this.position.z + this.size * 0.5
      );

      // Draw thrust trail
      renderer.drawLine(startPos, endPos, 'rgba(200, 200, 255, 0.5)', 3);

      // Draw impact point
      const alpha = 0.6 * (1 - progress);
      renderer.drawCircle(endPos, 8, `rgba(255, 255, 255, ${alpha})`);

    } else if (weaponType === 'dagger') {
      // Quick stab effect - small concentrated burst
      const stabProgress = Math.sin(progress * Math.PI);
      const attackPos = new Vector3(
        this.position.x + facing.x * range * 0.5 * stabProgress,
        this.position.y + facing.y * range * 0.5 * stabProgress,
        this.position.z + this.size * 0.4
      );
      const alpha = 0.7 * (1 - progress);
      renderer.drawCircle(attackPos, 6 + stabProgress * 4, `rgba(255, 200, 200, ${alpha})`);

    } else if (weaponType === 'hammer' || weaponType === 'club') {
      // Ground slam effect
      const slamProgress = progress > 0.5 ? (progress - 0.5) * 2 : 0;
      if (slamProgress > 0) {
        const attackPos = new Vector3(baseX, baseY, 0);
        const radius = range * 20 * slamProgress;
        const alpha = 0.5 * (1 - slamProgress);
        renderer.drawCircle(attackPos, radius, `rgba(139, 90, 43, ${alpha})`);
        // Inner impact
        renderer.drawCircle(attackPos, radius * 0.5, `rgba(255, 200, 100, ${alpha})`);
      }

    } else if (weaponType === 'scythe') {
      // Wide sweeping arc
      const sweepProgress = Math.sin(progress * Math.PI);
      const arcRadius = range * 18;

      // Draw multiple points along the arc
      for (let i = -2; i <= 2; i++) {
        const angle = Math.atan2(facing.y, facing.x) + (i * 0.4) * sweepProgress;
        const arcX = this.position.x + Math.cos(angle) * arcRadius / 16;
        const arcY = this.position.y + Math.sin(angle) * arcRadius / 16;
        const arcPos = new Vector3(arcX, arcY, this.position.z + this.size * 0.3);
        const alpha = 0.4 * (1 - progress) * (1 - Math.abs(i) * 0.2);
        renderer.drawCircle(arcPos, 6, `rgba(100, 200, 150, ${alpha})`);
      }

    } else {
      // Sword - slash arc effect
      const slashProgress = Math.sin(progress * Math.PI);

      // Draw arc trail
      for (let i = 0; i < 5; i++) {
        const t = i / 4;
        const angle = Math.atan2(facing.y, facing.x) + (t - 0.5) * Math.PI * 0.5 * slashProgress;
        const dist = range * 0.8;
        const arcX = this.position.x + Math.cos(angle) * dist;
        const arcY = this.position.y + Math.sin(angle) * dist;
        const arcPos = new Vector3(arcX, arcY, this.position.z + this.size * 0.5);
        const alpha = 0.5 * (1 - progress) * (1 - Math.abs(t - 0.5) * 1.5);
        const size = 5 + slashProgress * 3;
        renderer.drawCircle(arcPos, size, `rgba(255, 255, 255, ${alpha})`);
      }
    }
  }

  private onDeath(): void {
    console.log('Player died!');
    EventBus.publish({ type: 'PLAYER_DIED' });
  }

  private onDamage(_amount: number): void {
    this.health.setInvulnerable(0.5);
    // Screen shake, sound, etc. would go here
  }

  /** Get interaction position (in front of player) */
  getInteractionPosition(): Vector3 {
    const dir = this.movement.getFacingDirection();
    return new Vector3(
      this.position.x + dir.x * 1,
      this.position.y + dir.y * 1,
      this.position.z
    );
  }

  /** Enable or disable flying ability */
  setCanFly(canFly: boolean): void {
    this.canFly = canFly;
    if (!canFly) {
      this.isFlying = false;
      this.flyHeight = 0;
      this.position.z = 0;
    }
  }

  /** Set whether player is currently trying to fly (holding space) */
  setFlying(flying: boolean): void {
    if (this.canFly) {
      this.isFlying = flying;
    }
  }

  /** Check if player can fly */
  getCanFly(): boolean {
    return this.canFly;
  }

  /** Check if player is currently flying */
  getIsFlying(): boolean {
    return this.isFlying && this.flyHeight > 0;
  }

  /** Get current fly height */
  getFlyHeight(): number {
    return this.flyHeight;
  }

  /** Check if player is flying high enough to be safe from enemies */
  isSafeFromEnemies(): boolean {
    return this.flyHeight >= this.safeFlightHeight;
  }
}
